"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DropdownListbox = void 0;
exports.useDropdownListbox = useDropdownListbox;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _mergeRefs = _interopRequireDefault(require("merge-refs"));
var _composeEventHandlers = _interopRequireDefault(require("@ux/component-utilities/compose-event-handlers"));
var _isString = _interopRequireDefault(require("@ux/component-utilities/is-string"));
var _dropdown = require("./dropdown");
var _util = require("../util");
var _descendants = require("../descendants");
const _excluded = ["loop", "orientation", "onKeyDown", "ref"],
  _excluded2 = ["as"];
/* eslint-disable no-undefined */
/* eslint-disable complexity */
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @type ListboxHook
 * @property {object} data Derived data from Dropdown state
 * @property {string} data.activeDescendant HTMLElement id for the active item
 * @property {string} data.size What size variant should be used for the list
 * @property {string} data.triggerId HTMLElement id for the trigger element
 * @property {object} props Props to be spread onto element
 * @property {string} props.tabIndex HTMLElement tabIndex attribute
 * @property {string} props.ref Composed Ref
 * @property {string} props.id HTMLElement id
 * @property {string} props.onKeyDown Composed Keydown event handler
 * @property {string} "props.ia-activedescendant" HTMLElement id for the active list item
 * @property {string} "props.ia-labelledby" HTMLElement id for the trigger element
 */
/**
 * The container for all the items and is responsible for aria-descendant support.
 * @param {Object} props useDropdownListbox props
 * @param {string} [props.loop=true] Should the keyboard navigation loop through all items or stop at the beginning/end
 * @param {(horizontal|vertical)} [props.orientation=vertical] Orientation of the items
 * @param {string} props.onKeyDown Keydown event handler
 * @param {string} props.ref Forwarded Ref
 * @returns {ListboxHook} Data used to implement dropdown listbox
 */
function useDropdownListbox(_ref) {
  let {
      loop = true,
      orientation = 'vertical',
      onKeyDown,
      ref: forwardedRef
    } = _ref,
    props = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    closeOnSelect,
    dispatch,
    triggerRef,
    dropdownRef,
    selectCallbacks,
    listId,
    rtl,
    size,
    state: {
      isExpanded,
      triggerId,
      activeIndex,
      typeaheadQuery
    }
  } = (0, _dropdown.useDropdownContext)('useDropdownListbox');
  const items = (0, _dropdown.useDropdownDescendants)();
  const ref = (0, _mergeRefs.default)(dropdownRef, forwardedRef);

  // Support typeahead activating of item
  _react.default.useEffect(() => {
    // Respond to user char key input with typeahead
    const match = findItemFromTypeahead(items, typeaheadQuery);
    if (typeaheadQuery && match != null) {
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index: match,
          dropdownRef
        }
      });
    }
    const timeout = window.setTimeout(() => typeaheadQuery && dispatch({
      type: _dropdown.SEARCH_FOR_ITEM,
      payload: ''
    }), 1000);
    return () => window.clearTimeout(timeout);
  }, [dispatch, items, typeaheadQuery, dropdownRef]);

  // Handle dynamic updates to menu items
  const prevItemsLength = (0, _util.usePrevious)(items.length);
  const prevSelected = (0, _util.usePrevious)(items[activeIndex]);
  const prevactiveIndex = (0, _util.usePrevious)(activeIndex);
  _react.default.useEffect(() => {
    if (activeIndex > items.length - 1) {
      // If for some reason our selection index is larger than our possible
      // index range (let's say the last item is selected and the list
      // dynamically updates), we need to select the last item in the list.
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index: items.length - 1,
          dropdownRef
        }
      });
    } else if (
    // Checks if
    //  - item length has changed
    //  - selection index has not changed BUT selected item has changed
    //
    // This prevents any dynamic adding/removing of items from actually
    // changing a user's expected selection.
    prevItemsLength !== items.length && activeIndex > -1 && prevSelected && prevactiveIndex === activeIndex && items[activeIndex] !== prevSelected) {
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index: items.findIndex(i => i.key === (prevSelected === null || prevSelected === void 0 ? void 0 : prevSelected.key)),
          dropdownRef
        }
      });
    }
  }, [dropdownRef, dispatch, items, prevItemsLength, prevSelected, prevactiveIndex, activeIndex]);
  const handleKeyDown = (0, _composeEventHandlers.default)(function handleKeyDown(event) {
    const {
      key
    } = event;
    if (!isExpanded) {
      return;
    }
    switch (key) {
      case 'Enter':
      case ' ':
        // eslint-disable-next-line no-case-declarations
        const selected = items.find(item => item.index === activeIndex);
        // For links, the Enter key will trigger a click by default, but for
        // consistent behavior across items we'll trigger a click when the
        // spacebar is pressed.
        if (selected && !selected.disabled) {
          event.preventDefault();
          if (selected.isLink && selected.element) {
            selected.element.click();
          } else {
            var _selected$element;
            focus(triggerRef.current);
            selectCallbacks.current[selected.index] && selectCallbacks.current[selected.index](selected.key, {
              id: (_selected$element = selected.element) === null || _selected$element === void 0 ? void 0 : _selected$element.id,
              event
            });
            dispatch({
              type: _dropdown.SELECT_MENU_ITEM,
              payload: {
                index: selected.index
              }
            });
            if (closeOnSelect) {
              dispatch({
                type: _dropdown.CLOSE_MENU,
                payload: {
                  triggerRef
                }
              });
            }
          }
        }
        break;
      case 'Escape':
        focus(triggerRef.current);
        dispatch({
          type: _dropdown.CLOSE_MENU,
          payload: {
            triggerRef
          }
        });
        break;
      case 'Tab':
        // prevent leaving
        event.preventDefault();
        break;
      default:
        // Check if a user is typing some char keys and respond by setting
        // the query state.
        if ((0, _isString.default)(key) && key.length === 1) {
          const query = typeaheadQuery + key.toLowerCase();
          dispatch({
            type: _dropdown.SEARCH_FOR_ITEM,
            payload: query
          });
        }
        break;
    }
  }, (0, _descendants.useDescendantKeyDown)(_dropdown.DropdownDescendantContext, {
    activeItemIndex: activeIndex,
    orientation,
    loop,
    rtl: rtl,
    filter: item => !item.disabled,
    callback: _ref2 => {
      let {
        index,
        element
      } = _ref2;
      // Scroll active item into view when using keyboard navigation
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index,
          dropdownRef
        }
      });
    },
    key: 'index'
  }));
  return {
    data: {
      // eslint-disable-next-line no-undefined
      activeDescendant: (0, _dropdown.useItemId)(activeIndex) || undefined,
      size,
      triggerId
    },
    props: _objectSpread(_objectSpread({
      'tabIndex': -1
    }, props), {}, {
      ref,
      'id': listId,
      'onKeyDown': (0, _composeEventHandlers.default)(onKeyDown, handleKeyDown),
      'aria-activedescendant': (0, _dropdown.useItemId)(activeIndex) || undefined,
      'aria-labelledby': triggerId || undefined
    })
  };
}
const DropdownListbox = exports.DropdownListbox = /*#__PURE__*/_react.default.forwardRef((_ref3, forwardedRef) => {
  let {
      as: Comp = 'div'
    } = _ref3,
    rest = (0, _objectWithoutProperties2.default)(_ref3, _excluded2);
  const {
    props
  } = useDropdownListbox(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /*#__PURE__*/_react.default.createElement(Comp, props);
});
DropdownListbox.propTypes = {
  as: _propTypes.default.node
};
DropdownListbox.displayName = 'DropdownListbox';

/**
 * When a user's typed input matches the string displayed in an item, it is
 * expected that the matching item is selected. This is our matching function.
 *
 * @param {Array<Descendant>} items descendants
 * @param {string} query typeahead query
 * @returns {number|null} index of matching item
 */
function findItemFromTypeahead(items) {
  let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  if (!query) {
    return null;
  }
  const found = items.find(item => {
    var _item$element, _item$element$dataset, _item$element$dataset2;
    return item.disabled ? false : (_item$element = item.element) === null || _item$element === void 0 ? void 0 : (_item$element$dataset = _item$element.dataset) === null || _item$element$dataset === void 0 ? void 0 : (_item$element$dataset2 = _item$element$dataset.valuetext) === null || _item$element$dataset2 === void 0 ? void 0 : _item$element$dataset2.toLowerCase().startsWith(query);
  });
  return found ? items.indexOf(found) : null;
}